import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Button, useMultiStyleConfig, VStack } from "@chakra-ui/react";
import {
  Calendar,
  CalendarContext,
  CalendarControls,
  CalendarControlStyles,
  CalendarDate,
  CalendarDays,
  CalendarMonth,
  CalendarMonths,
  CalendarNextButton,
  CalendarPrevButton,
  CalendarWeek,
} from "@uselessdev/datepicker";
import { range } from "lodash-es";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { CalendarViewMonthYear } from "shared/components/input/date/CalendarViewMonthYear";

export interface CalendarDateRange {
  start?: Date;
  end?: Date;
}

export interface CalendarViewProps {
  value: Date | CalendarDateRange | undefined;
  onSelectDate?: (value: Date) => void;
  onSelectDateRange?: (value: CalendarDateRange) => void;
  months?: number;
}

const parseCalendarDate = (value: CalendarDate | undefined) =>
  value ? (value instanceof Date ? value : new Date(value)) : undefined;

export const CalendarView: React.FC<CalendarViewProps> = ({
  value,
  onSelectDate,
  onSelectDateRange,
  months = 1,
  ...props
}) => (
  <Calendar
    value={!value || value instanceof Date ? { start: value } : value}
    onSelectDate={(value) => {
      if (value instanceof Date) {
        onSelectDate?.(value);
      } else if (typeof value === "number") {
        onSelectDate?.(new Date(value));
      } else {
        onSelectDateRange?.({
          start: parseCalendarDate(value.start),
          end: parseCalendarDate(value.end),
        });
      }
    }}
    singleDateSelection={months === 1}
    allowOutsideDays={true}
    highlightToday={true}
    allowSelectSameDay={true}
    months={months}
    {...props}
  >
    <VStack tabIndex={-1} userSelect="none" alignItems="center">
      <CalendarControls>
        <CalendarPrevButton
          as={(props) => (
            <NavButton {...props}>
              <ChevronLeftIcon width="20px" height="20px" />
            </NavButton>
          )}
        />
        <CalendarNextButton
          as={(props) => (
            <NavButton {...props}>
              <ChevronRightIcon width="20px" height="20px" />
            </NavButton>
          )}
        />
      </CalendarControls>
      <CalendarMonths>
        {range(months).map((month) => (
          <CalendarMonth key={month} month={month}>
            <CalendarViewMonthYear />
            <CalendarWeek />
            <CalendarDays />
          </CalendarMonth>
        ))}
      </CalendarMonths>
      {months === 1 && <CalendarTodayButton />}
    </VStack>
  </Calendar>
);

const NavButton: React.FC<React.PropsWithChildren> = ({
  children,
  ...props
}) => {
  const styles = useMultiStyleConfig(
    "CalendarControl",
  ) as CalendarControlStyles;
  return (
    <Button
      sx={styles.button}
      variant="unstyled"
      borderWidth={0}
      width="40px"
      height="40px"
      shadow="none"
      {...props}
    >
      {children}
    </Button>
  );
};

export const CalendarTodayButton: React.FC = () => {
  const { onSelectDates } = useContext(CalendarContext);
  const { t } = useTranslation(["common"]);
  return (
    <Button
      size="xs"
      variant="ghost"
      flex={1}
      minWidth="157px"
      py="8px"
      borderWidth="1px"
      borderRadius="4px"
      mb="16px"
      mt="-15px"
      color="gray.600"
      height="auto"
      onClick={() => {
        onSelectDates(new Date());
      }}
    >
      {t("common:today")}
    </Button>
  );
};
